import { useApolloClient, useMutation } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, Flex, Radio, RadioGroup, Text } from '~/components/blocks';
import {
  MedicationFollowupSettingFragment,
  MedicationFollowupSettingFragmentDoc,
  UpdateMedicationFollowupStatusSettingDocument,
} from '~/graphql/generated/graphql';
import {
  editMedicationFollowupStatusDialogState,
  medicationFollowupSettingIdState,
} from '~/state/settings_messages/atoms';

import { SettingPopup } from './SettingPopup';
import { Fields } from './types';
import { validationSchema } from './validation';

const makeInitialValue = (
  medicationFollowupStatusSetting: MedicationFollowupSettingFragment | null,
): Fields => {
  if (!medicationFollowupStatusSetting) {
    return { statusManagement: false };
  }

  return {
    statusManagement: medicationFollowupStatusSetting.statusManagement,
  };
};

export const MedicationFollowupStatusEditDrawer = () => {
  const theme = useTheme();
  const apolloClient = useApolloClient();

  const [error, setError] = useState<string | null>(null);
  const [ankerElement, setAnkerElement] = useState<HTMLElement | null>(null);

  const { className } = useAtomValue(editMedicationFollowupStatusDialogState);
  const resetEditMedicationFollowupStatusDialogState = useResetAtom(
    editMedicationFollowupStatusDialogState,
  );
  const medicationFollowupSettingId = useAtomValue(medicationFollowupSettingIdState);
  const isOpen = !!className;

  const [updateMedicationFollowupStatusSetting, { loading: update }] = useMutation(
    UpdateMedicationFollowupStatusSettingDocument,
  );

  const medicationFollowupStatusSetting =
    apolloClient.readFragment<MedicationFollowupSettingFragment>({
      id: `MedicationFollowupSetting:${medicationFollowupSettingId}`,
      fragment: MedicationFollowupSettingFragmentDoc,
    });

  const initialValues = useMemo(
    () => makeInitialValue(medicationFollowupStatusSetting),
    [medicationFollowupStatusSetting],
  );

  const handleSubmit = useCallback(
    async (values: Fields) => {
      setError(null);

      try {
        await updateMedicationFollowupStatusSetting({
          variables: {
            input: {
              statusManagement: values.statusManagement,
            },
          },
        });
        resetEditMedicationFollowupStatusDialogState();
      } catch (error) {
        setError(error?.message || 'エラーが発生しました');
      }
    },
    [resetEditMedicationFollowupStatusDialogState, updateMedicationFollowupStatusSetting],
  );

  const handleClose = useCallback(() => {
    resetEditMedicationFollowupStatusDialogState();
  }, [resetEditMedicationFollowupStatusDialogState]);

  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (className) {
      const elem = document.querySelector<HTMLElement>(`.${className}`);

      if (elem) setAnkerElement(elem);
    }
  }, [className]);

  return (
    <SettingPopup
      open={isOpen}
      ankerRef={ankerElement}
      title="フォローアップ管理"
      onClose={handleClose}
    >
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          return (
            <>
              {error && (
                <Alert status="error" mb={theme.space.m}>
                  <Text size="s">{error}</Text>
                </Alert>
              )}
              <Box>
                <Text size="s">
                  設定を有効にすると、フォローアップをして
                  <br />
                  いる患者に「対応中」ラベルを表示します。
                </Text>
                <RadioGroup mt={theme.space.m}>
                  <Radio
                    label="有効"
                    disabled={update}
                    checked={formik.values.statusManagement}
                    onChange={() => formik.setFieldValue('statusManagement', true)}
                  />
                  <Radio
                    label="無効"
                    disabled={update}
                    checked={!formik.values.statusManagement}
                    onChange={() => formik.setFieldValue('statusManagement', false)}
                  />
                </RadioGroup>
              </Box>
              <Flex justifyContent="flex-end" mt={theme.space.m}>
                <Button
                  use="base"
                  loading={update}
                  disabled={!formik.dirty}
                  onClick={formik.submitForm}
                >
                  保存
                </Button>
              </Flex>
            </>
          );
        }}
      </Formik>
    </SettingPopup>
  );
};
